import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { pathOr } from 'ramda'
import {
  compose,
  defaultProps,
  fromRenderProps,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import withHeroText from 'components/HOCs/withHeroText'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralDisclosureRequired from 'components/HOCs/WithReferralDisclosureRequired'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import trackGAEvent from 'utils/analytics/trackGAEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError/getGeneralError'
import { withOptimizely } from 'utils/Optimizely'

import createLaterContributionMutation from '../gql/createLaterContribution.mutation.gql'
import initialStateQuery from '../gql/initialState.query.gql'
import makeInvestmentMutation from '../gql/makeInvestment.mutation.gql'
import { InnerProps, InvestmentMode } from '../types'
import { readUserSettings, saveUserSettings } from '../utilities'

export const DEFAULT_ONE_TIME = '5'

export const ONE_TIME_AMOUNTS_CORE = ['5', '10', '20', '50', '100']

const clearUserSettings = () => saveUserSettings()

export const investmentAmount = (props: any) => {
  return props.oneTimeAmount || DEFAULT_ONE_TIME
}

const enhance = compose<InnerProps, {}>(
  defaultProps({
    loading: true,
    investmentMode: InvestmentMode.OneTime,
  }),
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ hasVisited, isDynamicFunnelEnabled, routeToPage }) => ({
      hasVisited,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withFlowRouter,
  withReferralDisclosureRequired(),
  withRouter,
  withHeroText,
  withPartnerProps,
  withRemoteStrings,
  withApolloErrorModal,
  withOptimizely,
  withState(
    'oneTimeAmount',
    'setOneTimeAmount',
    readUserSettings('oneTimeAmount', DEFAULT_ONE_TIME),
  ),
  withState('confirmModalOpen', 'setConfirmModalOpen', false),
  withState('noFundingSourceModalOpen', 'setNoFundingSourceModalOpen', false),
  withState('submitting', 'setSubmitting', false),
  graphql(makeInvestmentMutation, { name: 'makeInvestment' }),
  graphql(createLaterContributionMutation, { name: 'createLaterContribution' }),
  graphql(initialStateQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data }) => {
      return {
        loading: data.loading,
        fundingSource: pathOr(null, ['fundingSourceInfo'], data),
        oneTimeAmount: readUserSettings('oneTimeAmount', DEFAULT_ONE_TIME),
        investmentMode: readUserSettings('investmentMode', InvestmentMode.OneTime),
        laterAccountId: pathOr(null, ['laterAccounts', 0, 'accountId'], data),
      }
    },
  }),
  withProps(({ getRemoteString, heroText }) => {
    return {
      header: getRemoteString('onetime_investment_header', heroText.header),
      text: getRemoteString('onetime_investment_text', heroText.text),
    }
  }),
  withHandlers<InnerProps, {}>({
    handleCTA: ({
      setConfirmModalOpen,
      setNoFundingSourceModalOpen,
      fundingSource,
      flow,
      partner,
    }) => () => {
      if (fundingSource) {
        setConfirmModalOpen(true)
        trackEvent('Screen Viewed', {
          eventData: {
            feature: 'Registration Initial Investment: One-Time',
            event_description: 'Confirm modal viewed',
            object_name: 'registrationInitialInvestmentOneTimeConfirmAmount',
            style: 'modal',
            screen: 'registrationInitialInvestmentOneTime',
            screen_name: 'registrationInitialInvestmentOneTimeConfirmAmount',
            object: 'Screen',
            action: 'Viewed',
            ...getDefaultEventData('Screen Viewed'),
          },
        })
      } else {
        setNoFundingSourceModalOpen(true)
      }
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.ONE_TIME_START_INVESTING),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })
    },
    handleSkip: ({
      route,
      referralDisclosureRequired,
      flow,
      partner,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => () => {
      saveUserSettings()
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.ONE_TIME_SKIP),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })

      if (isDynamicFunnelEnabled) {
        referralDisclosureRequired
          ? routeToPage(PageName.OneTimeInvestment, ActionType.Continue)
          : routeToPage(PageName.OneTimeInvestment, ActionType.Skip)
      } else {
        route({
          from: FlowRoute.OneTimeInvestment,
          action: 'continue',
          state: { referralDisclosureRequired },
        })
      }
    },
    handleLinkAccount: ({ route, isDynamicFunnelEnabled, routeToPage }) => () => {
      if (isDynamicFunnelEnabled) {
        routeToPage(PageName.OneTimeInvestment, ActionType.Link)
      } else {
        route({ from: FlowRoute.OneTimeInvestment, action: 'link-account' })
      }
    },
    handleOneTimeAmountChange: ({
      setOneTimeAmount,
      recurringAmount,
      investmentMode,
      flow,
      partner,
    }) => ({ currentTarget }) => {
      saveUserSettings({
        oneTimeAmount: currentTarget.dataset.value,
        recurringAmount,
        investmentMode,
      })
      setOneTimeAmount(currentTarget.dataset.value)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.CHANGE_ONE_TIME_AMOUNT),
          ...getDefaultEventData('Button Tapped'),
          cta_title: currentTarget.dataset.value,
        },
        flow,
        partner,
      })
    },
    handleModalClosed: ({ setConfirmModalOpen }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          feature: 'Registration Initial Investment: One-Time',
          event_description: 'Confirm modal dismissed',
          object_name: 'registrationInitialInvestmentOneTimeConfirmAmountCancel',
          style: 'default',
          screen: 'registrationInitialInvestmentOneTime',
          screen_name: 'registrationInitialInvestmentOneTimeConfirmAmount',
          object: 'Button',
          action: 'Tapped',
          ...getDefaultEventData('Button Tapped'),
        },
      })
      setConfirmModalOpen(false)
    },
    handleModalConfirmed: ({
      route,
      referralDisclosureRequired,
      makeInvestment,
      investmentMode,
      recurringAmount,
      oneTimeAmount,
      setConfirmModalOpen,
      setSubmitting,
      trackOptimizely,
      closeErrorModal,
      showErrorModal,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => () => {
      setConfirmModalOpen(false)
      setSubmitting(true)

      const amount = parseFloat(
        investmentAmount({ investmentMode, recurringAmount, oneTimeAmount }),
      )

      trackEvent('Button Tapped', {
        eventData: {
          feature: 'Registration Initial Investment: One-Time',
          event_description: 'Confirm modal confirmed',
          object_name: 'registrationInitialInvestmentOneTimeConfirmAmountConfirm',
          style: 'default',
          screen: 'registrationInitialInvestmentOneTime',
          screen_name: 'registrationInitialInvestmentOneTimeConfirmAmount',
          destination: 'registrationInitialInvestmentRecurring',
          functionality: 'makeInvestment',
          object: 'Button',
          action: 'Tapped',
          cta_title: 'OK',
          amount,
          ...getDefaultEventData('Button Tapped'),
        },
      })

      const makeOneTimeInvestment = () => {
        makeInvestment({
          variables: { amount },
        })
          .then(() => {
            clearUserSettings()
            trackOptimizely('one_time_investment.complete')
            trackGAEvent('oneTimeDepositScheduled')
            if (isDynamicFunnelEnabled) {
              referralDisclosureRequired
                ? routeToPage(PageName.OneTimeInvestment, ActionType.Continue)
                : routeToPage(PageName.OneTimeInvestment, ActionType.Skip)
            } else {
              route({
                from: FlowRoute.OneTimeInvestment,
                action: 'continue',
                state: { referralDisclosureRequired },
              })
            }
          })
          .catch(err => {
            sendToSentry(err)

            const retryHandler = () => {
              closeErrorModal()
              makeOneTimeInvestment()
            }
            const { title, message, actions, image } = getGeneralError(
              err,
              { closeErrorModal },
              retryHandler,
            )
            showErrorModal({ title, message, actions, image })
          })
          .finally(() => {
            setSubmitting(false)
          })
      }

      makeOneTimeInvestment()
    },
  }),
)

export default enhance
